import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import SettingLayout from "../SettingLayout";
import { RazorInputField } from "../../../../components/razor-reuse";
import TabLink from "../../../../components/fragment/tab-link/TabLink";
import NoContentPage from "../../../../layout/dashboard/no-content/NoContentPage";
import { Button } from "../../../../components/custom/custom-button";
import NumberInput from "./NumberInput";
import { getAllAdminUsers } from "../../../../redux/users";
import {
  getApprovalSettings,
  updateApprovalSettings,
} from "../../../../redux/settings";
import type { AppDispatch } from "../../../../redux/store";
import RootState, {
  ApprovalApproverUser,
  ApprovalSettingsPayload,
  ApprovalSettingsType,
} from "../../../../redux/type";

const APPROVAL_TYPE_TABS: { label: string; value: ApprovalSettingsType }[] = [
  { label: "Mobile", value: "mobile" },
  { label: "Business", value: "business" },
];

const MAX_APPROVAL_LEVELS = 5;
// Selects further up the page must sit above ones lower down, so an open
// dropdown never gets painted over by a field it visually overlaps. The
// base value just needs headroom above MAX_APPROVAL_LEVELS.
const SELECT_BASE_ZINDEX = 10;
const LEVEL_LETTERS = ["A", "B", "C", "D", "E"];
const LEVEL_COUNT_OPTIONS = Array.from(
  { length: MAX_APPROVAL_LEVELS },
  (_, i) => ({ label: String(i + 1), value: i + 1 })
);

interface UserOption {
  label: string;
  value: string;
}

interface ApprovalLevelRow {
  user: UserOption[];
  min_amount: string;
  max_amount: string;
}

const EMPTY_LEVEL: ApprovalLevelRow = {
  user: [],
  min_amount: "",
  max_amount: "",
};

function approverToOption(approver: ApprovalApproverUser | null): UserOption | null {
  if (!approver) return null;
  return {
    label: `${approver.first_name || ""} ${approver.last_name || ""}`.trim() || approver.email,
    value: approver.id,
  };
}

// Each level's cap must exceed the previous level's cap — levels are
// sequential bands, so a lower/equal cap would make this level unreachable.
// The final level never has a cap field (it catches everything above the
// last capped level), so there's nothing to validate there.
function getCapError(levels: ApprovalLevelRow[], index: number): string | null {
  if (index === 0 || index === levels.length - 1) return null;
  const previousCap = levels[index - 1].max_amount;
  const currentCap = levels[index].max_amount;
  if (previousCap === "" || currentCap === "") return null;
  if (Number(currentCap) <= Number(previousCap)) {
    return `Must be higher than Level ${index}'s cap (${Number(previousCap).toLocaleString()})`;
  }
  return null;
}

const ApprovalSettingsIndex = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { admin_users, loading_users } = useSelector(
    (state: RootState) => state.users
  );
  const {
    approval_settings,
    loading_approval_settings,
    mutation_approval_settings,
  } = useSelector((state: RootState) => state.settings);

  // "business" is both the backend's default and this page's original
  // (pre-tab) behaviour, so any unrecognized/missing value falls back to it.
  const approvalType: ApprovalSettingsType =
    searchParams.get("type") === "mobile" ? "mobile" : "business";

  const [levelCount, setLevelCount] = useState(1);
  const [levels, setLevels] = useState<ApprovalLevelRow[]>([
    { ...EMPTY_LEVEL },
  ]);
  const [loadError, setLoadError] = useState(false);

  const fetchApprovalSettings = async (type: ApprovalSettingsType) => {
    setLoadError(false);
    const result: any = await dispatch(getApprovalSettings({ type }) as any);
    if (!result?.payload?.data?.status) {
      setLoadError(true);
    }
  };

  useEffect(() => {
    if (admin_users?.length < 1) dispatch(getAllAdminUsers({}) as any);
  }, [dispatch, admin_users?.length]);

  useEffect(() => {
    fetchApprovalSettings(approvalType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalType]);

  const handleTabChange = (label: string) => {
    const tab = APPROVAL_TYPE_TABS.find((t) => t.label === label);
    if (!tab || tab.value === approvalType) return;
    // Clear the form immediately so the previous tab's numbers never flash
    // under the new tab while its settings are being fetched.
    setLevelCount(1);
    setLevels([{ ...EMPTY_LEVEL }]);
    setSearchParams({ type: tab.value });
  };

  useEffect(() => {
    if (!approval_settings) return;
    // The API doesn't guarantee `levels` is sorted by `level` — sort it so
    // array index reliably matches level order (the render/save logic below
    // relies on index: last index = final/uncapped level).
    const sortedLevels = [...(approval_settings.levels || [])].sort(
      (a, b) => a.level - b.level
    );
    const nextLevels: ApprovalLevelRow[] = sortedLevels.map((lvl) => ({
      user: (lvl.approvers || [])
        .map((approver) => approverToOption(approver))
        .filter((opt): opt is UserOption => opt !== null),
      min_amount: "",
      max_amount: String(lvl.cap_amount ?? ""),
    }));
    setLevelCount(approval_settings.number_of_levels || nextLevels.length || 1);
    setLevels(nextLevels.length ? nextLevels : [{ ...EMPTY_LEVEL }]);
  }, [approval_settings]);

  const handleLevelCountChange = (count: number) => {
    setLevelCount(count);
    setLevels((prev) => {
      const next = [...prev];
      while (next.length < count) next.push({ ...EMPTY_LEVEL });
      next.length = count;
      return next;
    });
  };

  const updateLevel = (
    index: number,
    field: keyof ApprovalLevelRow,
    value: any
  ) => {
    setLevels((prev) =>
      prev.map((level, i) => (i === index ? { ...level, [field]: value } : level))
    );
  };

  const hasCapErrors = levels.some(
    (_, index) => getCapError(levels, index) !== null
  );

  const handleCancel = () => {
    setLevelCount(1);
    setLevels([{ ...EMPTY_LEVEL }]);
  };

  const handleSave = () => {
    const lastIndex = levels.length - 1;
    const payload: ApprovalSettingsPayload = {
      type: approvalType,
      number_of_levels: levelCount,
      levels: levels.map((level, index) => ({
        level: index + 1,
        // The last (final) approver level has no cap — it catches everything above the previous level's cap.
        cap_amount: index === lastIndex ? null : Number(level.max_amount) || 0,
        approvers: (level.user || []).map((u) => u.value),
      })),
    };
    dispatch(updateApprovalSettings(payload) as any);
  };

  return (
    <SettingLayout hideBtn back={true} onBack={handleCancel} title="Approval Settings">
      <section className="w-4/5 space-y-[24px] pb-[40px]">
        <div className="max-w-[280px]">
          <TabLink
            list={APPROVAL_TYPE_TABS.map((tab) => ({
              label: tab.label,
              active: tab.value === approvalType,
            }))}
            onClick={(param) => handleTabChange(param?.label)}
          />
        </div>

        {loadError ? (
          <div className="flex flex-col items-center justify-center rounded-[8px] border border-[#FDECEC] bg-[#FFF9F9] py-[48px]">
            <p className="text-[14px] font-medium text-[#DC2626]">
              Couldn't load approval settings
            </p>
            <p className="mt-[4px] text-[13px] text-[#758089]">
              Something went wrong while fetching this setting.
            </p>
            <button
              type="button"
              onClick={() => fetchApprovalSettings(approvalType)}
              className="mt-[12px] rounded-[8px] border border-[#734792] px-[14px] py-[8px] text-[13px] font-medium text-[#734792]"
            >
              Retry
            </button>
          </div>
        ) : loading_approval_settings ? (
          <NoContentPage loading height="360px" />
        ) : (
          <>
            <div>
              <p className="text-[16px] font-semibold text-[#1E1E1E]">
                1. How many levels of approval
              </p>
              <div className="mt-[16px] max-w-[320px]">
                <RazorInputField
                  color="black-light"
                  type="select"
                  label="Input level"
                  placeholder="Select level"
                  style={{ position: "relative", zIndex: SELECT_BASE_ZINDEX }}
                  value={LEVEL_COUNT_OPTIONS.find((o) => o.value === levelCount)}
                  onChange={(e: any) => handleLevelCountChange(e?.value)}
                  selectOption={LEVEL_COUNT_OPTIONS}
                />
              </div>
            </div>

            {levels.map((level, index) => (
              <div key={index}>
                <p className="text-[14px] text-[#1E1E1E] mb-[16px]">
                  {LEVEL_LETTERS[index]}. Who should have approval power for Level{" "}
                  {index + 1}
                </p>
                <div className="space-y-[16px] max-w-[320px]">
                  <RazorInputField
                    color="black-light"
                    type="select"
                    label="User"
                    placeholder="Select user"
                    style={{ position: "relative", zIndex: SELECT_BASE_ZINDEX - (index + 1) }}
                    loadingSelect={loading_users}
                    value={level.user}
                    onChange={(e: any) => updateLevel(index, "user", e || [])}
                    selectOption={admin_users || []}
                    multi
                  />
                  {index !== levels.length - 1 && (
                    <div className="form-group form-group__black-light">
                      <label className="form-label">Cap Amt</label>
                      <NumberInput
                        thousandSeparator
                        placeholder="Cap Amt"
                        className="form-input"
                        value={level.max_amount}
                        onChange={(e) => updateLevel(index, "max_amount", e.target.value)}
                      />
                      {getCapError(levels, index) && (
                        <p className="mt-[4px] text-[12px] text-red-500">
                          {getCapError(levels, index)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <section className="flex items-center gap-[12px]">
              <Button variant="primary-outline" className="px-20" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                disabled={hasCapErrors}
                isLoading={mutation_approval_settings}
                onClick={handleSave}
                className="px-20"
              >
                Save
              </Button>
            </section>
          </>
        )}
      </section>
    </SettingLayout>
  );
};

export default ApprovalSettingsIndex;
